import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import Draggable from "react-draggable";
import "./CenterModal.scss";
import Iconify from "../iconify/Iconify";
import PropTypes from "prop-types";
import { ModalStackManager } from "../../../utils/ModalStackManager/ModalStackManager"; // Adjust path as needed

const CenterModal = ({
    isOpen,
    onClose,
    children,
    modalTitle,
    modalSize = "w-95",
    transition = "slideIn",
    transitionDirection = "fromBottom",
    className = " ",
    draggable = false,
    allowMaximize = false,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [showModal, setShowModal] = useState(isOpen);
    const [isMaximized, setIsMaximized] = useState(false);
    const [isTopModal, setIsTopModal] = useState(false);
    const modalRef = useRef(Symbol("modal"));
    const modalId = modalRef.current;

    useEffect(() => {
        if (isOpen) {
            ModalStackManager.addModal({ id: modalId, onClose });

            const updateTopStatus = (topModal) => {
                setIsTopModal(topModal?.id === modalId);
            };

            const unsubscribe = ModalStackManager.subscribe(updateTopStatus);
            updateTopStatus(ModalStackManager.getTopModal());

            ModalStackManager.initGlobalEscapeListener();

            return () => {
                ModalStackManager.removeModal(modalId);
                unsubscribe();
                setIsTopModal(false);
            };
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            setShowModal(true);
            setTimeout(() => setIsVisible(true), 10);
        } else {
            setIsVisible(false);
            setTimeout(() => setShowModal(false), 400);
        }
    }, [isOpen]);

    const toggleMaximized = () => setIsMaximized((prev) => !prev);

    if (!showModal) return null;

    const widthMatch = modalSize.match(/w-(\d+)/);
    const modalWidth = widthMatch ? `${widthMatch[1]}%` : "45%";
    const modalStyle = { width: modalWidth };

    let transitionClass = "";
    if (transition === "grow") {
        transitionClass = isVisible ? "grow-in" : "grow-out";
    } else if (transition === "bounce") {
        transitionClass = isVisible ? "bounce-in" : "bounce-out";
    } else {
        transitionClass = isVisible
            ? `${transition}-${transitionDirection}`
            : `${transition}-out-${transitionDirection}`;
    }

    const modalContent = (
        <div
            className={`center-modal ${transitionClass} ${isMaximized ? "maximized" : ""}`}
            style={modalStyle}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="center-modal-header">
                <h2>{modalTitle}</h2>
                <div className="action-btn-modal">
                    {allowMaximize && (
                        <button onClick={toggleMaximized} className="maximize-button">
                            <Iconify
                                icon={
                                    isMaximized
                                        ? "garden:minimize-stroke-12"
                                        : "garden:maximize-stroke-12"
                                }
                            />
                        </button>
                    )}
                    <button className="close-button" onClick={onClose}>
                        <Iconify icon="gg:close-o" width={24} />
                    </button>
                </div>
            </div>
            <div className="center-modal-body">{children}</div>
        </div>
    );

    const modalOverlay = (
        <div className={`center-modal-overlay ${className}`}>
            {draggable ? (
                <Draggable handle=".center-modal-header" disabled={!isTopModal}>
                    <div>{modalContent}</div>
                </Draggable>
            ) : (
                modalContent
            )}
        </div>
    );

    return ReactDOM.createPortal(
        <div data-modal-root>{modalOverlay}</div>,
        document.body
    );
};

CenterModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    children: PropTypes.node,
    modalTitle: PropTypes.string.isRequired,
    modalSize: PropTypes.string,
    transition: PropTypes.string,
    transitionDirection: PropTypes.string,
    className: PropTypes.string,
    draggable: PropTypes.bool,
    allowMaximize: PropTypes.bool,
};

export default CenterModal;
